import * as MUI from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MyButton from "../../../../components/buttons/MyButton";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import PetSelector from "../../../../components/form/PetSelector";
import UserSelector from "../../../../components/form/UserSelector";
import { AppointmentType } from "../../../../components/modals/bookingModalComponents/context/BookingModalContext";
import FormSection from "../../../../components/wrappers/FormSection";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import UserProvider, { UserContext } from "../../../../contexts/UserProvider";
import { BOOKING } from "../../../../enums/ApiPaths";
import {
  CREATE_TREATMENT,
  EDIT_APPOINTMENT,
} from "../../../../enums/PermissionTitles";
import {
  APPOINTMENT_AUDIT,
  APPOINTMENT_GENERAL,
} from "../../../../enums/QueryKeys";
import { USER } from "../../../../enums/Roles";
import useConfirm from "../../../../hooks/useConfirm";
import useUser, { useGetUserId } from "../../../../hooks/useUser";
import { getDateObject } from "../../../../utils/DateTimeRepository";
import { setDefaultValues } from "../../../../utils/Functions";
import { normalizePetGender } from "../../../pets/utils/Functions";
import AppointmentTimePicker from "../../form/AppointmentTimePicker";
import AppointmentTypePicker from "../../form/AppointmentTypePicker";
import usePutAppointments from "../../hooks/usePutAppointments";

//For the love of god we need to shorten this component it's unmanagable

export default function EditAppointment({
  appointmentProp,
  allPets,
  handleEditing,
}) {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { currentClinic, roles } = useUser();
  const loggedInUserId = useGetUserId();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const [newUserSelected, setNewUserSelected] = React.useState(false);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const url = BOOKING + loggedInUserId;

  const fullAppointment = {
    appointment: appointmentProp,
    usersPets: allPets,
  };

  const put = usePutAppointments(
    url,
    APPOINTMENT_GENERAL + id,
    fullAppointment
  );
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel")
  );

  const { handleSnackbar } = React.useContext(SnackbarContext);
  const methods = useForm();

  const appointment = appointmentProp;
  const templateCondition =
    isPermissionAllowed(CREATE_TREATMENT) && appointment?.treatment;

  React.useEffect(() => {
    if (appointment !== undefined) {
      const { pet, ...appointmentWithoutPet } = appointment;

      if (pet) {
        methods.setValue("pet", pet);
      }

      setDefaultValues(appointmentWithoutPet, methods.setValue);

      methods.setValue(
        "startDate",
        dayjs(getDateObject(appointment?.startDate))
      );

      methods.setValue("time", dayjs(getDateObject(appointment?.startDate)));
      methods.setValue("description", appointment?.description || "");

      if (roles?.adminOrEmployee) {
        methods.setValue(
          "endTime",
          dayjs(getDateObject(appointment?.endDate)).format("HH:mm")
        );
      }

      if (appointment[AppointmentType]) {
        const currentLangName =
          appointment[AppointmentType].translations?.[currentLang];
        const defaultValue = currentLangName
          ? currentLangName
          : appointment[AppointmentType].title;
        const duration = appointment?.[AppointmentType]?.duration ?? 30;
        methods.setValue(AppointmentType, {
          id: appointment[AppointmentType].id,
          title: defaultValue,
          mandatroyDesc: appointment[AppointmentType]?.mandatoryDescription,
          duration: duration,
        });
      }

      methods.setValue(
        "time",
        dayjs(getDateObject(appointment?.startDate)).format("HH:mm")
      );

    }
  }, [appointment, currentLang, i18n.language, methods, roles]);

  // The pets dropdown only contains pets of users currently in this clinic. If
  // the appointment's user has since changed clinic, their pets (from the
  // appointment details) must still be selectable since the appointment
  // itself is still tied to this clinic
  const appointmentUserPets = React.useMemo(() => {
    const appointmentUser = appointment?.user;
    if (appointmentUser?.role !== USER) return [];

    const pets = (allPets ?? []).map((pet) => ({
      ...pet,
      user: appointmentUser,
    }));
    const appointmentPet = appointment?.pet;
    if (
      appointmentPet?.id != null &&
      !pets.some((pet) => Number(pet.id) === Number(appointmentPet.id))
    ) {
      pets.push({ ...appointmentPet, user: appointmentUser });
    }
    return pets;
  }, [allPets, appointment?.user, appointment?.pet]);

  const getPetByGivenId = (id) => {
    return allPets.find((pet) => {
      return pet.id == id;
    });
  };

  const submitData = async () => {
    const prepare = methods.watch();
    if (typeof prepare.pet === "number") {
      prepare.pet = getPetByGivenId(prepare.pet);
    }
    if (
      prepare.pet == "" ||
      prepare?.pet?.name == "" ||
      prepare?.pet?.type == undefined
    ) {
      delete prepare.pet;
    }
    const appointement = prepareObjectForSubmit(prepare);
    const res = await put.submitCallback(appointement);
    if (res == true) {
      handleEditing();
      queryClient.invalidateQueries([APPOINTMENT_AUDIT + appointment?.id]);
    }
  };


  function prepareObjectForSubmit(appointement) {
    if (appointement.template) {
      delete appointement.template;
    }
    var new_obj = {
      ...appointement,
      startDate: getStartDate(appointement),
      endDate:getEndDate(appointement),
      priceList: appointement[AppointmentType],
    };

    if (appointement?.pet) {
      const birthdate = appointement?.pet?.birthdate;
      const pet = {
        ...appointement?.pet,
        birthdate: dayjs.isDayjs(birthdate)
          ? birthdate.format("DD/MM/YYYY")
          : birthdate,
        gender: normalizePetGender(appointement?.pet?.gender),
      };
      new_obj = {
        ...new_obj,
        pet: pet,
      };
    }
    return checkTimes(new_obj);
  }

  function getEndDate(appointment) {
    // If no endTime is set in the form, return null (backend handles duration)
    if (!appointment.endTime) {
      return null;
    }

    let dateObj = dayjs(getDateObject(appointment.startDate));
    const endTimeStr = appointment.endTime;

    const dateString = dateObj.format("DD/MM/YYYY");
    return dateString + ", " + endTimeStr + ":00";
  }

  function checkTimes(obj) {
    if (dayjs(obj.time).isSame(dayjs(obj.endTime))) {
      methods.setError(
        "endTime",
        { type: "focus", message: t("InvalidEndTime") },
        { shouldFocus: true }
      );
    } else {
      delete obj.time;
      delete obj.endTime;
      return obj;
    }
  }

function getStartDate(appointment) {
    if (appointment.time || !appointment.startDate) {
      return parseStartDate(appointment);
    } else {
      return appointment.startDate;
    }
  }

  function parseStartDate(appointment) {
    const appointmenet_date = dayjs(
      getDateObject(appointment.startDate)
    ).format("DD/MM/YYYY");

    return appointmenet_date + ", " + appointment.time + ":00";
  }

  const toggleNewUserSelected = () => {
    setNewUserSelected((prev) => !prev);
  };

  if (!allPets) {
    return <MUI.LinearProgress />;
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitData)}>
        <MUI.Stack spacing={3}>
          <FormSection title={t("DateAndTimeOfAppointment")}>
            <AppointmentTimePicker testId={"appointment.edit-form"} />
          </FormSection>
          <FormSection title={t("Service")}>
            <AppointmentTypePicker labelOff={true} rows={10} testId={"appointment.edit-form"} />
          </FormSection>

          {roles.adminOrEmployee ? (
            <UserProvider>
              <FormSection title={t("UserInformation")}>
                <UserSelectorView
                  toggleNewUserSelected={toggleNewUserSelected}
                  newUserSelected={newUserSelected}
                />
              </FormSection>
              <FormSection title={t("PetInformation")}>
                <PetSelector
                  pet={appointment?.pet}
                  additionalPets={appointmentUserPets}
                  isNewUserSelected={newUserSelected}
                  testId={"appointment.edit-form.pet-select"}
                />
              </FormSection>
            </UserProvider>
          ) : null}
          <ConfirmationDialog />
          <MyButton
            disabed={String(!isPermissionAllowed(EDIT_APPOINTMENT))}
            type="submit"
            variant="contained"
            message={t("Save")}
            data-testid={"appointment.edit-form.save-button"}
          />
        </MUI.Stack>
      </form>
    </FormProvider>
  );
}

function UserSelectorView({ toggleNewUserSelected, newUserSelected }) {
  const {
    getAutoCompleteUsers,
    isLoading: userLoading,
    error: errorFetchingUsers,
  } = React.useContext(UserContext);

  if (userLoading) {
    return <MUI.LinearProgress />;
  } else if (errorFetchingUsers && !userLoading) {
    return <SimpleAlert message={"ErrorFetchingUsers"} severity={"error"} />;
  }

  return (
    <UserSelector
      toggleNewUserSelected={toggleNewUserSelected}
      isNewUserSelected={newUserSelected}
      label={"SelectUser"}
      users={getAutoCompleteUsers()}
      testId={"appointment.edit-form.user-select"}
    />
  );
}
